/**
 * The one AudioContext, its master chain, and the iOS unlock.
 *
 * Nothing here is constructed at module load: a browser that never gets a user
 * gesture must never end up with a suspended context sitting around, and the
 * test suite imports this module without an AudioContext existing at all.
 *
 * Every sound in the app is synthesized. That is not a purity exercise — the app
 * is an offline-first PWA whose whole shell is precached, and audio files would
 * be the single largest thing in it. The reference implementation this replaces
 * shipped 18 MB of MP3s for the same eight cues.
 */

type MaybeContext = typeof AudioContext | undefined;

interface Chain {
  ctx: AudioContext;
  /** Everything routes through here, so one gain governs the whole app. */
  master: GainNode;
  /** One second of white noise, reused by every noise-based voice. */
  noise: AudioBuffer;
}

let chain: Chain | null = null;
let unavailable = false;

export type AudioState = 'unavailable' | 'locked' | 'running';

export function audioState(): AudioState {
  if (unavailable) return 'unavailable';
  if (!chain) return 'locked';
  return chain.ctx.state === 'running' ? 'running' : 'locked';
}

/** Called when the state changes, so the mute button can show "tap to enable". */
let onStateChange: (() => void) | null = null;

export function watchAudioState(listener: (() => void) | null): void {
  onStateChange = listener;
}

/**
 * Build or resume the audio graph. MUST be called synchronously from inside a
 * real user-gesture handler.
 *
 * The ordering below is load-bearing and is covered by a test that asserts it
 * completes before the microtask queue runs. Safari grants a gesture exactly one
 * synchronous window to start audio: an `await` — even `await ctx.resume()` —
 * spends it, and the reference implementation this replaces failed precisely
 * there. So: no await, no .then(), nothing async before start().
 */
export function primeAudio(): void {
  if (unavailable) return;

  try {
    if (!chain) {
      const Ctor: MaybeContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (!Ctor) {
        unavailable = true;
        onStateChange?.();
        return;
      }

      // 1. Construct inside the gesture — this is what starts it 'running'
      //    rather than 'suspended' on Safari.
      const ctx = new Ctor();

      // 2. Fire and forget. Awaiting this is the bug.
      void ctx.resume();

      // 3. Start a real source. A one-frame silent buffer is the canonical
      //    unlock; an <audio> element with no src unlocks nothing, and even with
      //    a src it is a different permission track from WebAudio.
      const silent = ctx.createBufferSource();
      silent.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      silent.connect(ctx.destination);
      silent.start(0);

      // 4. The master chain: one gain for everything, and a gentle lowpass so
      //    the noise voices are not fizzy through a phone speaker.
      const master = ctx.createGain();
      master.gain.value = 0.9;
      const softener = ctx.createBiquadFilter();
      softener.type = 'lowpass';
      softener.frequency.value = 12_000;
      master.connect(softener);
      softener.connect(ctx.destination);

      // 5. Only now the noise buffer. Filling it takes about a millisecond, but
      //    doing it after start() means it can never push start() out of the
      //    gesture window.
      const noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const samples = noise.getChannelData(0);
      for (let i = 0; i < samples.length; i++) {
        samples[i] = Math.random() * 2 - 1;
      }

      ctx.onstatechange = () => onStateChange?.();
      chain = { ctx, master, noise };
      onStateChange?.();
      return;
    }

    // Already built. iOS can drop a context back to suspended after a call or a
    // Bluetooth switch, which is why the gesture listeners stay armed for the
    // life of the page rather than firing once.
    if (chain.ctx.state !== 'running') {
      void chain.ctx.resume();
    }
  } catch {
    // A browser that refuses to construct a context is not a reason to break the
    // game; it just plays silently.
    unavailable = true;
    onStateChange?.();
  }
}

/**
 * Run something against a live audio graph, or do nothing.
 *
 * Voices never null-check: if there is no context, or it is locked, or the tab is
 * hidden, this is a no-op. The hidden check matters — websocket frames keep
 * arriving on a backgrounded phone, and a pocket that starts playing card sounds
 * is a bug report.
 */
export function withAudio(run: (chain: Chain) => void): void {
  if (!chain || chain.ctx.state !== 'running') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

  try {
    run(chain);
  } catch {
    // A single failed voice must never take a round down.
  }
}

/** The context's clock, for the scheduler. Zero when there is no context. */
export function audioNow(): number {
  return chain ? chain.ctx.currentTime : 0;
}

/** Test seam: forget the graph so the next primeAudio() rebuilds it. */
export function resetAudioForTests(): void {
  chain = null;
  unavailable = false;
  onStateChange = null;
}
